import LeadsImportModal from '@/components/Leads/LeadsImportModal';
import { StyledButton } from '@/constants/themes';
import useAlchemystStoreForAi from '@/hooks/ai/client/useAlchemystStoreForAi';
import { useSession } from '@/hooks/useSession';
import {
  ConvertedData,
  Lead,
  RenderMessageProps,
} from '@/types/components/converse/messages';
import { SuccessAILead } from '@/types/leads';
import { fetchWithRewrites } from '@/utils/fetchWithRewrites';
import { prettyPrintObject } from '@/utils/leads';
import {
  CheckCircle,
  ContentCopy,
  ForkRight,
  OpenInNew,
} from '@mui/icons-material';
import {
  Avatar,
  Box,
  IconButton,
  keyframes,
  Modal,
  Typography,
  useTheme,
} from '@mui/material';
import { useEffect, useState } from 'react';
import ChatModal from '../ChatModal';
import FilesSection from './files-section';
import MarkdownRender from './MarkdownRenderer';

// Define typing animation
const blink = keyframes`
  0% { opacity: 0.2; }
  20% { opacity: 1; }
  100% { opacity: 0.2; }
`;

// ReactMarkdown config
const reactMarkdownConfig = {
  p: ({ children }: React.PropsWithChildren) => (
    <Typography
      sx={{
        lineHeight: 1.6,
        my: 1,
        // '& code': {
        //   backgroundColor: themeMode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
        //   padding: '2px 4px',
        //   borderRadius: 1,
        //   fontSize: '0.9em',
        // },
      }}
      variant="body1"
    >
      {children}
    </Typography>
  ),
  pre: ({ children }: React.PropsWithChildren) => (
    <Box
      component="pre"
      sx={{
        // backgroundColor: themeMode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
        padding: 2,
        borderRadius: 1,
        overflow: 'auto',
        fontSize: '0.9em',
        maxWidth: '40vw',
        maxHeight: '40vh',
        fontFamily: 'monospace',
        wordWrap: 'break-word',
        overflowWrap: 'break-word',
        my: 1,
        '& code': {
          fontFamily: 'monospace',
        },
      }}
    >
      {children}
    </Box>
  ),
  code: ({ children }: React.PropsWithChildren) => (
    <Box
      component="pre"
      sx={{
        // backgroundColor: themeMode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
        padding: 2,
        borderRadius: 1,
        overflow: 'auto',
        fontSize: '0.9em',
        maxWidth: '40vw',
        maxHeight: '40vh',
        fontFamily: 'monospace',
        wordWrap: 'break-word',
        overflowWrap: 'break-word',
        my: 1,
        '& code': {
          fontFamily: 'monospace',
        },
      }}
    >
      {children}
    </Box>
  ),
};

// Define step type
interface ThinkingStep {
  type: string;
  content: string;
  error?: any;
  icon?: string;
  parentUpdate?: string; // Reference to parent thinking update
}

// Define step type for timeline steps
interface TimelineStep {
  type: string;
  content: string | Record<string, any> | null;
  error: Error | null;
  timestamp?: string;
  parentUpdate?: string; // Reference to parent thinking update
}

function RenderMessage({
  role,
  message,
  json,
  animate,
  isThinking,
  thinkingProcess,
  thinking_steps,
  steps,
  isAccordionOpen = false,
  onAccordionToggle,
  messageId,
  currentChatId,
  isSideBarOpen,
}: RenderMessageProps): React.ReactNode {
  const theme = useTheme();
  const themeMode = useAlchemystStoreForAi((store) => store.theme);
  const [messageIndex, setMessageIndex] = useState<boolean>(false);
  const [fileUrls, setFileUrls] = useState<string[]>([]);

  useEffect(() => {
    console.log('isAccordionOpen changed:', {
      isAccordionOpen,
      messageIndex,
      messageId,
    });
    if (typeof isAccordionOpen === 'boolean') {
      setMessageIndex(isAccordionOpen);
    }
  }, [isAccordionOpen]);

  const handleAccordionToggle = () => {
    const newState = !messageIndex;
    console.log('Accordion toggled:', { newState, messageId, messageIndex });
    setMessageIndex(newState);

    if (onAccordionToggle && messageId) {
      console.log(
        'Calling parent onAccordionToggle with:',
        messageId,
        newState
      );
      onAccordionToggle(messageId, newState);
    } else {
      console.log('Could not call onAccordionToggle:', {
        onAccordionToggle,
        messageId,
      });
    }
  };

  const {
    alchemyst,
    setStoreState,
    snackbarEntries,
    localLeads,
    documentViewer,
  } = useAlchemystStoreForAi((store) => ({
    alchemyst: store.alchemyst,
    localLeads: store.localLeads,
    setStoreState: store.setStoreState,
    snackbarEntries: store.snackbarEntries,
    documentViewer: store.documentViewer,
  }));
  // const user = useSession().session?.user;
  const userData = useSession();

  const [displayedMessage, setDisplayedMessage] = useState('');
  const [leadsImportModalOpen, setLeadsImportModalOpen] = useState(false);
  const [chatEmailModalOpen, setChatEmailModalOpen] = useState(false);
  const [localFields, setLocalFields] = useState<string[]>([]);
  const [isLeadsAvailable, setIsLeadsAvailable] = useState(false);
  const [forkModalOpen, setForkModalOpen] = useState(false);
  const [forkingAction, setForkingAction] = useState(false);

  const setLocalLeads = (leads: Lead[]) => {
    setStoreState({ localLeads: leads });
  };

  const MAX_WIDTH = '50vw';

  // Extract file URLs from steps
  useEffect(() => {
    if (steps && steps.length > 0) {
      const urls: string[] = [];
      for (const step of steps) {
        if (step.type === 'data_update_file' && step.content && !step.error) {
          if (typeof step.content === 'string') {
            urls.push(step.content);
          }
        }
      }
      if (urls.length > 0) {
        setFileUrls(urls);
      }
    }
  }, [steps]);

  const convertJsonToForm = (jsonInput: string): ConvertedData | null => {
    try {
      const { searchResults = [], query = {} } = JSON.parse(jsonInput);
      if (!searchResults.data?.length) return null;
      const fields = Object.keys(searchResults.data[0]);
      const leads = searchResults.data.map((item: Record<string, any>) =>
        fields.reduce(
          (acc: Lead, field) => ({
            ...acc,
            [field]: prettyPrintObject(item[field]),
          }),
          {}
        )
      );
      return { leads, fields, query };
    } catch (error) {
      console.error('Invalid JSON format:', error);
      return null;
    }
  };

  useEffect(() => {
    if (json) {
      const convertedData = convertJsonToForm(json);
      if (convertedData) {
        setIsLeadsAvailable(true);
        const { leads, fields } = convertedData;
        setLocalLeads(leads);
        setLocalFields(fields);
        if (Object.keys(convertedData.query).includes('searchParams')) {
          setStoreState({
            prospectorQuery: convertedData.query as SuccessAILead,
          });
        }
      }
    }
  }, [json]);

  useEffect(() => {
    if (role === 'assistant' && animate) {
      let index = 0;
      const interval = setInterval(() => {
        setDisplayedMessage(
          (prevMessage) => prevMessage + message[index++] || ''
        );
        if (index >= message.length) clearInterval(interval);
      }, 10);
    } else {
      setDisplayedMessage(message);
    }
  }, [animate, message, role]);

  const handleCopyToClipboard = () => {
    setStoreState({
      snackbarEntries: [
        ...snackbarEntries,
        {
          message: 'Message copied to clipboard!',
          severity: 'success',
          type: 'alert',
          notificationId: crypto.randomUUID(),
        },
      ],
    });
    navigator.clipboard.writeText(displayedMessage);
  };

  const handleForkAction = () => {
    setForkModalOpen(true);
  };

  const handleFork = async (messageId?: string) => {
    if (!messageId) {
      setStoreState({
        snackbarEntries: [
          ...snackbarEntries,
          {
            message: 'No message ID found for forking.',
            severity: 'error',
            type: 'alert',
            notificationId: crypto.randomUUID(),
          },
        ],
      });
      return;
    }
    setForkingAction(true);
    const forkResponse = await fetchWithRewrites('/api/v1/chat/fork', {
      method: 'POST',
      body: JSON.stringify({
        messageId,
        chatId: currentChatId,
      }),
      headers: {
        'Content-Type': 'application/json',
      },
    });
    if (forkResponse.ok) {
      const forkedChat: {
        forkedChatId: string;
        originalChatId: string;
        forkedAtMessageId: string;
      } = await forkResponse.json();
      const forkedChatId = forkedChat.forkedChatId;

      const forkedChatUrl = `/converse?chatId=${forkedChatId}`;

      window.open(forkedChatUrl, '_blank');
    } else {
      const errorResponse = forkResponse.statusText;
      console.log('\n\nForking error response:\n', errorResponse);
      setStoreState({
        snackbarEntries: [
          ...snackbarEntries,
          {
            message: JSON.stringify(errorResponse),
            severity: 'error',
            type: 'alert',
            notificationId: crypto.randomUUID(),
          },
        ],
      });
    }
    setForkingAction(false);
  };

  const getFileName = (url: string): string => {
    try {
      const urlObj = new URL(url);
      const pathSegments = urlObj.pathname.split('/');
      const filename = pathSegments[pathSegments.length - 1] || 'document';
      // Truncate filename if longer than 20 characters
      return filename.length > 20
        ? filename.substring(0, 17) + '...'
        : filename;
    } catch (e) {
      return 'document';
    }
  };

  const handleOpenFile = (url: string) => {
    // Get existing file URLs
    const existingUrls = documentViewer.fileUrls || [];

    // Check if the URL already exists in the tabs using exact URL matching
    const existingIndex = existingUrls.findIndex((fileUrl) => fileUrl === url);

    // If URL exists, just switch to that tab
    if (existingIndex >= 0) {
      const updatedViewer = {
        ...documentViewer,
        isOpen: true,
        activeTabIndex: existingIndex,
      };

      setStoreState({
        documentViewer: updatedViewer,
      });
      return;
    }

    // If URL doesn't exist, add it as a new tab and switch to it
    const updatedViewer = {
      ...documentViewer,
      fileUrls: [...existingUrls, url],
      isOpen: true,
      activeTabIndex: existingUrls.length, // New tab will be at the end
    };

    setStoreState({
      documentViewer: updatedViewer,
    });
  };

  const timelineColor = theme.palette.primary.contrastText;

  const handleOpenLeadsPreview = () => {
    setLeadsImportModalOpen(true);
  };

  // Render thinking process steps
  const renderThinkingSteps = () => {
    // Get steps from either active thinking process or completed thinking_steps
    const thinkingUpdates = isThinking
      ? (thinkingProcess || []).map((step) => ({
          type: 'thinking_update',
          content: step,
        }))
      : ((thinking_steps as ThinkingStep[]) || []).filter(
          (step) => step.type === 'thinking_update'
        );

    // Get thinking_progress steps from steps array
    const thinkingProgress = (steps || []).filter(
      (step) => step.type === 'thinking_progress'
    ) as TimelineStep[];

    if (!thinkingUpdates?.length) return null;

    // Group progress steps with their parent updates
    // Create a structure to organize progress items under their parent update
    const organizedUpdates = thinkingUpdates.map((update, index) => {
      // Find progress items that belong to this update using parentUpdate field if available
      const relatedProgress = thinkingProgress.filter((progress) => {
        // If progress has parentUpdate field that matches this update's content
        if (progress.parentUpdate && progress.parentUpdate === update.content) {
          return true;
        }

        // Fallback: If no parentUpdate specified, last update gets all remaining progress items
        if (!progress.parentUpdate && index === thinkingUpdates.length - 1) {
          return true;
        }

        return false;
      });

      return {
        update,
        progress: relatedProgress,
      };
    });

    return (
      <Box
        sx={{
          mt: 0.5,
          pl: 0,
          mb: 3,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          maxWidth: '100%', // Ensure it doesn't exceed the container width
        }}
      >
        {organizedUpdates.map((item, mainIndex) => (
          <Box
            key={`update-group-${mainIndex}`}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              width: '100%',
            }}
          >
            {/* Render thinking update */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                opacity: 0.7,
                py: 0.25,
                px: 2,
                borderRadius: 10,
              }}
            >
              {/* {!isThinking || mainIndex < thinkingUpdates.length - 1 ? (
                <CheckCircle
                  sx={{
                    fontSize: '0.8rem',
                    color: theme.palette.success.main,
                    opacity: 0.8,
                  }}
                />
              ) : (
                <CircularProgress
                  size={10}
                  sx={{
                    color: theme.palette.primary.main,
                    opacity: 0.8,
                  }}
                />
              )} */}
              <Typography>{item.update.content}</Typography>
            </Box>

            {/* Render progress items nested under this update */}
            {item.progress.length > 0 && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                  ml: 4,
                  pl: 2,
                  pb: 1,
                  pt: 0.5,
                  borderLeft: `1px solid ${themeMode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
                }}
              >
                {item.progress.map((step, index) => (
                  <Box
                    key={`progress-${mainIndex}-${index}`}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      py: 0.25,
                      px: 1.5,
                      borderRadius: 4,
                      width: 'fit-content',
                      maxWidth: '75%',
                      backgroundColor:
                        themeMode === 'dark'
                          ? 'rgba(55, 65, 81, 0.1)'
                          : 'rgba(229, 231, 235, 0.5)',
                      border: `1px solid ${
                        themeMode === 'dark'
                          ? 'rgba(55, 65, 81, 0.2)'
                          : 'rgba(209, 213, 219, 0.8)'
                      }`,
                    }}
                  >
                    <CheckCircle
                      sx={{
                        fontSize: '0.7rem',
                        color: theme.palette.info.main,
                        opacity: 0.8,
                        flexShrink: 0,
                      }}
                    />
                    <Box
                      sx={{
                        maxWidth: '100%',
                        fontFamily: 'monospace',
                        overflow: 'hidden',
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: '0.8rem',
                          opacity: 0.9,
                          fontWeight: 400,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          width: '100%',
                          display: 'block',
                        }}
                        variant="caption"
                      >
                        {typeof step.content === 'string'
                          ? step.content
                          : JSON.stringify(step.content)}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        ))}
      </Box>
    );
  };

  // Render typing indicator for assistant messages
  const renderTypingIndicator = () => {
    if (role === 'assistant' && isThinking) {
      return (
        <Box
          sx={{
            display: 'flex',
            gap: 0.7,
            mt: 1.5,
            alignItems: 'center',
            height: 30,
          }}
        >
          <Typography sx={{ opacity: 0.7, mr: 1 }} variant="caption">
            Thinking
          </Typography>
          {[0, 1, 2].map((i) => (
            <Box
              key={`dot-${i}`}
              sx={{
                width: 8,
                height: 8,
                backgroundColor: theme.palette.text.secondary,
                borderRadius: '50%',
                opacity: 0.7,
                animation: `${blink} 1.4s infinite ease-in-out`,
                animationDelay: `${i * 0.2}s`,
              }}
            />
          ))}
        </Box>
      );
    }
    return null;
  };

  return (
    <Box
      alignItems="flex-start"
      display="flex"
      justifyContent={role === 'user' ? 'flex-end' : 'flex-start'}
      maxWidth={MAX_WIDTH}
      pb={role === 'user' ? 2 : 1.5}
      position="relative"
      sx={{
        overflow: 'hidden',
        margin: '0 auto',
        width: '100%',
        mt: role === 'assistant' ? 0 : 2,
      }}
      textAlign={role === 'user' ? 'right' : 'left'}
    >
      {role !== 'user' && (
        <Box sx={{ position: 'relative' }}>
          <Avatar
            src={alchemyst.avatarUrl}
            sx={{
              zIndex: 1,
              width: 32,
              height: 32,
            }}
          >
            {alchemyst.name[0]}
          </Avatar>
          <Typography
            sx={{
              position: 'absolute',
              top: '-18px',
              left: '50%',
              transform: 'translateX(-50%)',
              fontSize: '0.65rem',
              color: theme.palette.text.secondary,
              opacity: 0.7,
              whiteSpace: 'nowrap',
              fontFamily: 'monospace',
            }}
            variant="caption"
          >
            {new Date().toLocaleTimeString([], {
              hour: '2-digit',
              minute: '2-digit',
            })}
          </Typography>
        </Box>
      )}
      <Box
        boxShadow="0 1px 2px rgba(0,0,0,0.05)"
        display="flex"
        flexDirection="column"
        mt={1}
        px={2.5}
        py={2}
        sx={{
          width: role === 'user' ? '70%' : '100%',
          maxWidth: role === 'user' ? '70%' : '100%',
          borderRadius: 2,
          zIndex: 1,
          height: 'auto',
          textAlign: role === 'user' ? 'right' : 'left',
        }}
      >
        <Box
          display="flex"
          flexDirection={role === 'user' ? 'row-reverse' : 'row'}
          // alignItems="center"
          gap={2}
          sx={
            {
              // textAlign: role === 'user' ? 'right' : 'left',
            }
          }
        >
          <Typography
            sx={{
              display: 'flex',
              fontSize: '0.8rem',
              alignContent: 'flex-end',
              flexDirection: role === 'user' ? 'row-reverse' : 'row',
              opacity: 0.8,
              mr: 0.5,
            }}
            variant="caption"
          >
            {role !== 'user' ? alchemyst.name : userData.data?.given_name}
          </Typography>
          <Box alignItems="center" display="flex">
            {!!currentChatId && !!messageId && role !== 'user' && (
              <IconButton
                onClick={handleForkAction}
                size="small"
                sx={{
                  mx: 0.5,
                  p: 0,
                  '&:hover': {
                    backgroundColor: 'transparent',
                  },
                }}
              >
                <ForkRight
                  fontSize="medium"
                  sx={{
                    width: 12,
                    height: 12,
                    opacity: 0.6,
                    '&:hover': {
                      opacity: 1,
                    },
                  }}
                />
              </IconButton>
            )}
            <IconButton
              onClick={handleCopyToClipboard}
              size="small"
              sx={{
                mx: 0.5,
                p: 0,
                '&:hover': {
                  backgroundColor: 'transparent',
                },
              }}
            >
              <ContentCopy
                fontSize="small"
                sx={{
                  width: 12,
                  height: 12,
                  opacity: 0.6,
                  '&:hover': {
                    opacity: 1,
                  },
                }}
              />
            </IconButton>
          </Box>
        </Box>
        {renderThinkingSteps()}
        <Box
          sx={{
            mt: thinkingProcess?.length || thinking_steps?.length ? 1.5 : 0,
            '& > .chat-message': {
              '& > p:first-of-type': {
                mt: 0,
              },
              '& > p:last-of-type': {
                mb: 0,
              },
            },
          }}
        >
          <MarkdownRender className="chat-message">
            {displayedMessage.replaceAll('\n', '\n\n')}
          </MarkdownRender>
          {/* <MarkdownRenderTest /> */}
        </Box>

        {/* Typing indicator */}
        {renderTypingIndicator()}

        {/* Action buttons */}
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            mt:
              isLeadsAvailable ||
              displayedMessage.toLowerCase().includes('subject:')
                ? 2
                : 0,
          }}
        >
          {isLeadsAvailable && (
            <StyledButton
              color="inherit"
              onClick={handleOpenLeadsPreview}
              sx={{
                borderRadius: 1.5,
                py: 0.5,
                px: 1.5,
                fontSize: '0.75rem',
                height: 28,
              }}
              variant="outlined"
            >
              See leads preview <OpenInNew sx={{ ml: 1, fontSize: '0.9rem' }} />
            </StyledButton>
          )}
          <Box display="flex" flexDirection="column" gap={1} mt={2}>
            {fileUrls.length > 0 && (
              <FilesSection
                filesToShow={Math.min(
                  6,
                  Math.max(2, Math.floor(fileUrls.length / 2))
                )}
                onChipClick={handleOpenFile}
                urls={fileUrls}
              />
            )}
          </Box>
          {displayedMessage.toLowerCase().includes('subject:') && (
            <StyledButton
              color="inherit"
              onClick={() => setChatEmailModalOpen(true)}
              sx={{
                borderRadius: 1.5,
                py: 0.5,
                px: 1.5,
                fontSize: '0.75rem',
                height: 28,
              }}
              variant="outlined"
            >
              Send email <OpenInNew sx={{ ml: 1, fontSize: '0.9rem' }} />
            </StyledButton>
          )}
        </Box>
      </Box>

      {role === 'user' && (
        <Box sx={{ position: 'relative', ml: 2 }}>
          <Avatar
            src={userData.data?.picture || ''}
            sx={{
              zIndex: 1,
              width: 32,
              height: 32,
            }}
          >
            U
          </Avatar>
          <Typography
            sx={{
              position: 'absolute',
              top: '-18px',
              left: '50%',
              transform: 'translateX(-50%)',
              fontSize: '0.65rem',
              color:
                themeMode === 'dark'
                  ? 'rgba(255,255,255,0.5)'
                  : 'rgba(0,0,0,0.5)',
              whiteSpace: 'nowrap',
            }}
            variant="caption"
          >
            {new Date().toLocaleTimeString([], {
              hour: '2-digit',
              minute: '2-digit',
            })}
          </Typography>
        </Box>
      )}

      <LeadsImportModal
        localFields={localFields}
        localLeads={localLeads}
        open={leadsImportModalOpen}
        setOpen={setLeadsImportModalOpen}
      />
      <ChatModal
        emailContent={displayedMessage}
        open={chatEmailModalOpen}
        setOpen={setChatEmailModalOpen}
      />
      <Modal
        onClose={() => setForkModalOpen(false)}
        open={forkModalOpen}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box
          sx={{
            backgroundColor: theme.palette.background.paper,
            padding: 4,
            borderRadius: 2,
            width: '400px',
            boxShadow: 24,
          }}
        >
          <Typography sx={{ mb: 2 }} variant="h6">
            Do you want to fork the chat?
          </Typography>
          <Typography variant="body1">
            Forking the chat will create a new session with the current chat
            history, upto the selected message.
            <br></br>
            <br></br>
            The forked chat will be opened in a new tab.
          </Typography>
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              mt: 3,
              justifyContent: 'flex-end',
            }}
          >
            <StyledButton
              disabled={!!forkingAction}
              onClick={() => {
                setForkModalOpen(false);
              }}
              variant="outlined"
            >
              Close
            </StyledButton>
            <StyledButton
              disabled={forkingAction}
              onClick={() => {
                void handleFork(messageId);
              }}
              variant="contained"
            >
              Fork
            </StyledButton>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
}

export default RenderMessage;
